/**
 * TourSpotlight
 *
 * Renders a full-screen semi-transparent overlay with a spotlight cutout
 * around the target element identified by the current step's CSS selector.
 *
 * When no target is provided (center placement) the overlay is a simple
 * semi-transparent backdrop.  When a target exists the cutout is produced
 * via box-shadow so the element shows through clearly.
 *
 * All overlay elements are pointer-events: none so user interaction with
 * the highlighted element is unblocked.
 */

import { useEffect, useRef, useState } from 'react';
import type { TourStep } from '@/types/product-tour';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourSpotlightProps {
  step: TourStep;
  isActive: boolean;
}

/** Returns true when the element is fully within the current viewport */
function isFullyVisible(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth
  );
}

export function TourSpotlight({ step, isActive }: TourSpotlightProps) {
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const hasScrolledRef = useRef(false);
  const padding = step.spotlightPadding ?? 8;

  useEffect(() => {
    if (!isActive || !step.target) {
      setRect(null);
      hasScrolledRef.current = false;
      return;
    }

    function measure() {
      if (!step.target) return;
      const el = document.querySelector(step.target);
      if (!el) return;

      const r = el.getBoundingClientRect();
      setRect({
        top: r.top + window.scrollY - padding,
        left: r.left + window.scrollX - padding,
        width: r.width + padding * 2,
        height: r.height + padding * 2,
      });
    }

    // Scroll into view on first mount only (not on every resize)
    if (!hasScrolledRef.current && step.target) {
      const el = document.querySelector(step.target);
      if (el && !isFullyVisible(el)) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      hasScrolledRef.current = true;
    }

    measure();

    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [isActive, step.target, padding]);

  if (!isActive) return null;

  // ── Center placement: simple backdrop only ────────────────────────────────
  if (!step.target || step.placement === 'center') {
    return (
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          backgroundColor: 'rgba(0,0,0,0.30)',
          pointerEvents: 'none',
        }}
      />
    );
  }

  // ── Targeted element: spotlight cutout ───────────────────────────────────
  return (
    <>
      {/* Full-screen overlay with spotlight hole via box-shadow */}
      {rect && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            zIndex: 9999,
            borderRadius: 6,
            pointerEvents: 'none',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.40)',
          }}
        />
      )}

      {/* Teal highlight border with pulse */}
      {rect && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            zIndex: 10000,
            borderRadius: 6,
            border: '2px solid #2dd4bf',
            pointerEvents: 'none',
            animation: 'tour-pulse 2s cubic-bezier(0.4,0,0.6,1) infinite',
          }}
        />
      )}

      {/* Pulse animation keyframes injected once */}
      <style>{`
        @keyframes tour-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.55; }
        }
      `}</style>
    </>
  );
}
